export interface SyncStats {
  created?: number;
  updated?: number;
  skipped?: number;
  errors?: number;
  deleted?: number;
  // Supervisions only: rows that inherited their billing state from the old
  // per-coding supervisions they replace.
  migrated?: number;
}

export interface SyncResponse {
  stats?: SyncStats | null;
  coding_requests_generation?: { contractsCreated?: number } | null;
  // Assignment changes the sync noticed but deliberately did not apply, listed on the
  // Modifications page for a human to decide on.
  total_changes_detected?: number | null;
}

/**
 * What a sync actually did, as a list of outcomes.
 *
 * Only non-zero ones are listed, so a run with nothing to do says so instead of reciting
 * four zeros. Separate from `syncSummary` because a caller syncing several entities at
 * once has to name each one — "Codeurs : 1 mis à jour" — and cannot reuse a sentence
 * that already opens with "Synchronisation terminée".
 */
export const syncOutcomes = (response: SyncResponse): string[] => {
  const stats = response.stats ?? {};
  const parts: string[] = [];

  if (stats.created) parts.push(`${stats.created} créé(s)`);
  if (stats.updated) parts.push(`${stats.updated} mis à jour`);
  if (stats.deleted) parts.push(`${stats.deleted} supprimé(s)`);
  if (stats.migrated) parts.push(`${stats.migrated} reprise(s) de l'ancien modèle`);
  if (stats.errors) parts.push(`${stats.errors} en erreur`);

  const contractsCreated =
    response.coding_requests_generation?.contractsCreated ?? 0;
  if (contractsCreated) parts.push(`${contractsCreated} contrat(s) créé(s)`);

  // A detected change is an outcome even though no row moved: the sync refuses to
  // follow a reassignment on invoiced or contracted work, so the counts above can all
  // be zero while there is something waiting on the Modifications page. Without this
  // the toast said "aucun changement" precisely when a decision was needed.
  const detected = response.total_changes_detected ?? 0;
  if (detected)
    parts.push(`${detected} modification(s) à traiter dans « Modifications »`);

  return parts;
};

/**
 * The same outcomes as one sentence, for a page syncing a single entity.
 *
 * The counts used to be computed by the sync and then dropped by the route, so every run
 * — one that wrote nothing, and one that rewrote thousands of rows — reported the same
 * fixed "Codages synchronisés". The per-record detail is in Journaux de Synchronisation.
 */
export const syncSummary = (
  response: SyncResponse,
  noChangeLabel = "aucun changement",
): string => {
  const parts = syncOutcomes(response);

  if (parts.length === 0) {
    return `Synchronisation terminée : ${noChangeLabel}`;
  }

  return `Synchronisation terminée : ${parts.join(", ")}`;
};
